import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
	ArrowLeft,
	ArrowRight,
	Check,
	Home,
	FileText,
	Printer,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
	useGetMedicalRecordByPatient,
	useGetMedicalRecordSession,
	useGetPatient,
	useGetSessions,
} from '@/services';
import { PRONTUARIO_BG_CLASS } from '@/pages/professional/sessao-chamada/constants/prontuarioTheme';
import { resolveSessionParticipantTarget } from '@/pages/professional/sessao-chamada/utils/prontuarioPatientSummary';
import { formatProntuarioSessionDate } from '@/pages/professional/sessao-chamada/utils/formatProntuarioSessionDate';
import {
	buildProntuarioSessionPrintFields,
	buildProntuarioSessionPrintPatientData,
} from '@/pages/professional/home/utils/buildProntuarioSessionPrintPayload';
import { printProntuarioSession } from '@/pages/professional/home/utils/prontuarioSessionPrint';
import type { OpenProntuarioNavigationState } from '@/pages/professional/home/utils/professionalHomeNavigation';
import { ProntuarioEditableSessionDetailView } from '@/pages/professional/home/components/ProntuarioEditableSessionDetailView';
import { ProntuarioEditableMedicalRecordView } from '@/pages/professional/home/components/ProntuarioEditableMedicalRecordView';
import type { ProntuarioEditorState } from '@/pages/professional/home/components/prontuarioEditableFields';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/utils/errorMessage';
import { readTutorialDemonstracaoReviewCache } from '@/pages/professional/tutorial-demonstracao/readTutorialDemonstracaoReviewCache';
import {
	TutorialDemonstracaoCallCoachBalloon,
	TUTORIAL_DEMO_REVIEW_FIRST_ADD_TAG_SELECTOR,
	TUTORIAL_DEMO_REVIEW_SESSION_FIELDS_SELECTOR,
	TUTORIAL_DEMO_REVIEW_TAG_EDITOR_SELECTOR,
	TUTORIAL_DEMO_REVIEW_ADVANCE_SELECTOR,
	TUTORIAL_DEMO_REVIEW_RECORD_FIELDS_SELECTOR,
	TUTORIAL_DEMO_REVIEW_EVOLUIR_MODAL_SELECTOR,
} from '@/pages/professional/tutorial-demonstracao/TutorialDemonstracaoCallCoachBalloon';
import {
	SessionEndReviewConfirmModal,
	SESSION_END_EVOLUIR_MIN_WAIT_MS,
} from './SessionEndReviewConfirmModal';

type ReviewStep = 'session' | 'record' | 'success';

type SessionEndProntuarioReviewProps = {
	session: Sessions.Session;
	onGoHome: () => void;
	onViewProntuario: (options: OpenProntuarioNavigationState) => void;
	/** Demo/tutorial: salva localmente sem chamar a API. */
	demoMode?: boolean;
};

const FADE_TRANSITION = { duration: 0.45, ease: [0.32, 0.72, 0, 1] as const };

const SCROLL_CLASS = cn(
	'overflow-y-auto overscroll-contain [scrollbar-width:thin]',
	'[scrollbar-color:rgba(148,163,184,0.4)_transparent]',
	'[&::-webkit-scrollbar]:w-1.5',
	'[&::-webkit-scrollbar-track]:bg-transparent',
	'[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-600/60',
);

/**
 * Revisão do prontuário recém-gerado por IA, exibida como overlay logo após a
 * animação de fim de sessão. Permite editar a sessão (e, na 1ª sessão, também o
 * histórico + plano terapêutico) antes de evoluir o prontuário.
 */
const SessionEndProntuarioReview = ({
	session,
	onGoHome,
	onViewProntuario,
	demoMode = false,
}: SessionEndProntuarioReviewProps) => {
	const queryClient = useQueryClient();
	const patientId = useMemo(
		() => resolveSessionParticipantTarget(session).patientId,
		[session],
	);
	const recordSessionIdFromSession =
		session.medical_record_session?.id ?? null;

	const demoReviewData = useMemo(
		() =>
			demoMode ? readTutorialDemonstracaoReviewCache(queryClient) : null,
		[demoMode, queryClient],
	);

	const { data: medicalRecordFromApi } = useGetMedicalRecordByPatient(
		patientId ?? '',
		{ enabled: !!patientId && !demoMode },
	);
	const medicalRecord = demoReviewData?.medicalRecord ?? medicalRecordFromApi;
	const recordId = demoReviewData?.recordId ?? medicalRecord?.id ?? '';
	const recordSessionId =
		demoReviewData?.recordSessionId ?? recordSessionIdFromSession ?? '';

	const {
		data: sessionDetailFromApi,
		isLoading: isSessionLoadingFromApi,
		isError: isSessionErrorFromApi,
	} = useGetMedicalRecordSession(recordId, recordSessionId, {
		enabled: !!recordId && !!recordSessionId && !demoMode,
		pollMode: 'post-process',
	});
	const sessionDetail = demoReviewData?.sessionDetail ?? sessionDetailFromApi;
	const isSessionLoading = demoMode ? false : isSessionLoadingFromApi;
	const isSessionError = demoMode
		? !demoReviewData?.sessionDetail
		: isSessionErrorFromApi;

	const { data: sessionsDataFromApi } = useGetSessions(
		{ isMe: true, patient_id: patientId ?? '', page: 1, limit: 50 },
		{ enabled: !!patientId && !demoMode },
	);
	const isFirstSession = demoReviewData
		? demoReviewData.isFirstSession
		: (sessionsDataFromApi?.total ?? 0) === 1;

	const sessionNumber =
		sessionDetail?.session_number ??
		session.medical_record_session?.session_number ??
		null;

	const { data: patient } = useGetPatient(patientId ?? '', {
		enabled: !!patientId && !demoMode,
	});

	const sessionDateLabel = useMemo(() => {
		if (!session.selected_date) return null;
		return formatProntuarioSessionDate(session.selected_date);
	}, [session.selected_date]);

	const scrollRef = useRef<HTMLDivElement>(null);
	const [step, setStep] = useState<ReviewStep>('session');
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isPrinting, setIsPrinting] = useState(false);
	const [showSessionFieldsCoach, setShowSessionFieldsCoach] = useState(
		() => demoMode,
	);
	const [showSessionTagsCoach, setShowSessionTagsCoach] = useState(false);
	const [showSessionTagEditorCoach, setShowSessionTagEditorCoach] =
		useState(false);
	const [showSessionAdvanceCoach, setShowSessionAdvanceCoach] =
		useState(false);
	const [showRecordFieldsCoach, setShowRecordFieldsCoach] = useState(false);
	const [showRecordAdvanceCoach, setShowRecordAdvanceCoach] = useState(false);
	const [showConfirmCoach, setShowConfirmCoach] = useState(false);

	const hasCompletedSessionTourRef = useRef(false);
	const hasCompletedRecordFieldsCoachRef = useRef(false);

	const resumeSessionAdvanceCoach = useCallback(() => {
		if (!demoMode || !hasCompletedSessionTourRef.current) return;

		setShowSessionFieldsCoach(false);
		setShowSessionTagsCoach(false);
		setShowSessionTagEditorCoach(false);
		setShowSessionAdvanceCoach(true);
	}, [demoMode]);

	const resumeRecordAdvanceCoach = useCallback(() => {
		if (!demoMode || !hasCompletedRecordFieldsCoachRef.current) return;

		setShowRecordFieldsCoach(false);
		setShowRecordAdvanceCoach(true);
	}, [demoMode]);

	const openRecordStepCoaches = useCallback(() => {
		if (!demoMode) return;

		if (hasCompletedRecordFieldsCoachRef.current) {
			resumeRecordAdvanceCoach();
			return;
		}

		setShowRecordFieldsCoach(true);
	}, [demoMode, resumeRecordAdvanceCoach]);

	const [sessionEditor, setSessionEditor] =
		useState<ProntuarioEditorState | null>(null);
	const [recordEditor, setRecordEditor] =
		useState<ProntuarioEditorState | null>(null);

	const handleSessionEditorState = useCallback(
		(state: ProntuarioEditorState | null) => setSessionEditor(state),
		[],
	);
	const handleRecordEditorState = useCallback(
		(state: ProntuarioEditorState | null) => setRecordEditor(state),
		[],
	);

	const handleAdvance = useCallback(() => {
		if (demoMode) {
			setShowSessionAdvanceCoach(false);
			setShowRecordAdvanceCoach(false);
		}
		if (step === 'session' && isFirstSession) {
			if (demoMode) {
				hasCompletedSessionTourRef.current = true;
			}
			setStep('record');
			if (demoMode) {
				openRecordStepCoaches();
			}
			return;
		}
		setConfirmOpen(true);
		if (demoMode) {
			setShowConfirmCoach(true);
		}
	}, [demoMode, step, isFirstSession, openRecordStepCoaches]);

	const handleBack = useCallback(() => {
		setStep('session');
		resumeSessionAdvanceCoach();
	}, [resumeSessionAdvanceCoach]);

	const handleDemoFirstTagEditorOpen = useCallback(() => {
		setShowSessionTagsCoach(false);
		setShowSessionTagEditorCoach(true);
	}, []);

	const showAdvanceCoachAfterTour = useCallback(() => {
		hasCompletedSessionTourRef.current = true;
		setShowSessionFieldsCoach(false);
		setShowSessionTagsCoach(false);
		setShowSessionTagEditorCoach(false);
		setShowSessionAdvanceCoach(true);
	}, []);

	const handleDemoTagEditorClose = useCallback(() => {
		showAdvanceCoachAfterTour();
	}, [showAdvanceCoachAfterTour]);

	const completeRecordFieldsCoach = useCallback(() => {
		hasCompletedRecordFieldsCoachRef.current = true;
		setShowRecordFieldsCoach(false);
		setShowRecordAdvanceCoach(true);
	}, []);

	const showReviewSessionFieldsCoach =
		demoMode &&
		showSessionFieldsCoach &&
		step === 'session' &&
		!isSessionLoading &&
		!isSessionError;

	const showReviewSessionTagsCoach =
		demoMode &&
		showSessionTagsCoach &&
		!showSessionTagEditorCoach &&
		!showSessionFieldsCoach &&
		step === 'session' &&
		!isSessionLoading &&
		!isSessionError;

	const showReviewSessionTagEditorCoach =
		demoMode &&
		showSessionTagEditorCoach &&
		step === 'session' &&
		!isSessionLoading &&
		!isSessionError;

	const showReviewSessionAdvanceCoach =
		demoMode &&
		showSessionAdvanceCoach &&
		step === 'session' &&
		!isSessionLoading &&
		!isSessionError;

	const showReviewRecordFieldsCoach =
		demoMode &&
		showRecordFieldsCoach &&
		step === 'record' &&
		isFirstSession;

	const showReviewRecordAdvanceCoach =
		demoMode &&
		showRecordAdvanceCoach &&
		step === 'record' &&
		isFirstSession &&
		!showRecordFieldsCoach;

	const showReviewConfirmCoach = demoMode && showConfirmCoach && confirmOpen;

	useEffect(() => {
		if (step === 'success') return;
		scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
	}, [step]);

	useEffect(() => {
		if (step !== 'session') {
			setShowSessionFieldsCoach(false);
			setShowSessionTagsCoach(false);
			setShowSessionTagEditorCoach(false);
			setShowSessionAdvanceCoach(false);
		}
		if (step !== 'record') {
			setShowRecordFieldsCoach(false);
			setShowRecordAdvanceCoach(false);
		}
	}, [step]);

	useEffect(() => {
		if (!showReviewSessionFieldsCoach && !showReviewRecordFieldsCoach)
			return;

		scrollRef.current?.scrollTo({ top: 0 });
	}, [showReviewSessionFieldsCoach, showReviewRecordFieldsCoach]);

	const handleCancelConfirm = useCallback(() => {
		setConfirmOpen(false);
		setShowConfirmCoach(false);
		if (!demoMode) return;

		if (step === 'record') {
			resumeRecordAdvanceCoach();
			return;
		}

		if (step === 'session') {
			resumeSessionAdvanceCoach();
		}
	}, [demoMode, step, resumeRecordAdvanceCoach, resumeSessionAdvanceCoach]);

	const handleConfirmSave = useCallback(async () => {
		if (demoMode) {
			setShowConfirmCoach(false);
			setConfirmOpen(false);
			setStep('success');
			return;
		}

		setIsSaving(true);
		try {
			const minWait = new Promise<void>((resolve) => {
				window.setTimeout(resolve, SESSION_END_EVOLUIR_MIN_WAIT_MS);
			});
			const saveWork = (async () => {
				await sessionEditor?.saveAsync();
				if (isFirstSession) {
					await recordEditor?.saveAsync();
				}
			})();

			await Promise.all([minWait, saveWork]);
			setConfirmOpen(false);
			setStep('success');
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao evoluir prontuário. Tente novamente.',
				),
			);
		} finally {
			setIsSaving(false);
		}
	}, [demoMode, sessionEditor, recordEditor, isFirstSession]);

	const handlePrintSession = useCallback(() => {
		if (!sessionDetail) {
			toast.error('Não foi possível preparar a impressão.');
			return;
		}

		setIsPrinting(true);
		try {
			const printed = printProntuarioSession({
				patient: buildProntuarioSessionPrintPatientData(
					patient,
					session,
				),
				session: {
					number: sessionNumber,
					dateLabel: sessionDateLabel,
				},
				fields: buildProntuarioSessionPrintFields(sessionDetail),
			});

			if (!printed) {
				toast.error(
					'Não foi possível preparar a impressão. Tente novamente.',
				);
			}
		} finally {
			setIsPrinting(false);
		}
	}, [patient, session, sessionDateLabel, sessionDetail, sessionNumber]);

	const handleViewProntuario = useCallback(() => {
		if (!patientId || !recordSessionId) return;

		onViewProntuario({
			patientId,
			medicalRecordSessionId: recordSessionId,
			platformSessionId: session.id,
		});
	}, [onViewProntuario, patientId, recordSessionId, session.id]);

	const stepTitle =
		step === 'record'
			? 'Histórico e plano terapêutico'
			: `Prontuário da sessão ${sessionNumber ?? '—'}`;
	const stepSubtitle =
		step === 'record'
			? 'Revise o histórico clínico e o plano gerados nesta primeira sessão.'
			: null;

	return (
		<motion.div
			className={cn(
				'absolute inset-0 z-50 flex flex-col overflow-hidden',
				PRONTUARIO_BG_CLASS,
			)}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={FADE_TRANSITION}
		>
			{step === 'success' ? (
				<div className="flex flex-1 items-center justify-center p-6">
					<motion.div
						className="flex w-full max-w-[420px] flex-col items-center text-center"
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={FADE_TRANSITION}
					>
						<motion.span
							className="mb-5 flex size-16 items-center justify-center rounded-full bg-[#8ab4f8] text-[#0f1722]"
							initial={{ scale: 0 }}
							animate={{ scale: [0, 1.2, 1] }}
							transition={{
								duration: 0.5,
								ease: 'easeOut',
								times: [0, 0.6, 1],
							}}
						>
							<Check size={30} strokeWidth={3} />
						</motion.span>
						<h2 className="text-[20px] font-semibold text-[#e8eaed]">
							{demoMode
								? 'Demonstração concluída!'
								: 'Prontuário evoluído com sucesso!'}
						</h2>
						{demoMode ? (
							<p className="mt-3 text-[14px] leading-relaxed text-[#9aa0a6]">
								Parabéns! Você concluiu a demonstração da
								videochamada e da revisão do prontuário com IA.
							</p>
						) : null}
						<div className="mt-8 flex w-full flex-col gap-3">
							{demoMode ? (
								<button
									type="button"
									onClick={onGoHome}
									className="flex w-full items-center justify-center rounded-full bg-[#8ab4f8] py-3 font-bold text-[#0f1722] transition-colors hover:bg-[#9cc1fa]"
								>
									Finalizar
								</button>
							) : (
								<>
									<button
										type="button"
										onClick={handleViewProntuario}
										disabled={
											!patientId || !recordSessionId
										}
										className="flex w-full items-center justify-center gap-2 rounded-full bg-[#8ab4f8] py-3 font-bold text-[#0f1722] transition-colors hover:bg-[#9cc1fa] disabled:opacity-60"
									>
										<FileText
											className="size-4"
											strokeWidth={2.5}
											aria-hidden
										/>
										Ver prontuário
									</button>
									<button
										type="button"
										onClick={handlePrintSession}
										disabled={!sessionDetail || isPrinting}
										className="flex w-full items-center justify-center gap-2 rounded-full border border-white/10 py-3 font-semibold text-slate-300 transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-60"
									>
										<Printer
											className="size-4"
											strokeWidth={2.5}
											aria-hidden
										/>
										{isPrinting
											? 'Preparando impressão...'
											: 'Imprimir'}
									</button>
									<button
										type="button"
										onClick={onGoHome}
										className="flex w-full items-center justify-center gap-2 rounded-full border border-white/10 py-3 font-semibold text-slate-300 transition-colors hover:bg-white/[0.06]"
									>
										<Home
											className="size-4"
											strokeWidth={2.5}
											aria-hidden
										/>
										Ir para tela inicial
									</button>
								</>
							)}
						</div>
					</motion.div>
				</div>
			) : (
				<>
					<div
						ref={scrollRef}
						className={cn(
							'min-h-0 flex-1',
							showReviewSessionFieldsCoach ||
								showReviewRecordFieldsCoach
								? 'overflow-hidden'
								: SCROLL_CLASS,
						)}
					>
						<div
							className={cn(
								'sticky top-0 z-10 px-6 pb-4 pt-8',
								PRONTUARIO_BG_CLASS,
							)}
						>
							<div className="mx-auto w-full max-w-[760px]">
								<p className="text-[12px] font-semibold uppercase tracking-wide text-[#8ab4f8]/75">
									{isFirstSession && step === 'record'
										? 'Etapa 2 de 2'
										: isFirstSession
										  ? 'Etapa 1 de 2'
										  : 'Revisão'}
								</p>
								<h2 className="font-albert mt-1 text-[22px] font-medium leading-tight text-[#e8eaed]">
									{stepTitle}
								</h2>
								{stepSubtitle ? (
									<p className="mt-1 text-[14px] leading-relaxed text-[#9aa0a6]">
										{stepSubtitle}
									</p>
								) : null}
							</div>
						</div>

						<div className="relative z-0 px-6">
							<div className="mx-auto w-full max-w-[760px] pb-28 pt-2">
								<div
									className={cn(
										'transition-opacity duration-300',
										step === 'session'
											? 'opacity-100'
											: 'hidden opacity-0',
									)}
								>
									<ProntuarioEditableSessionDetailView
										recordId={recordId}
										sessionId={recordSessionId}
										sessionNumber={sessionNumber}
										record={sessionDetail ?? null}
										isLoading={isSessionLoading}
										isError={isSessionError}
										variant="dark"
										autoSaveTags={false}
										hideSessionMeta
										demoMode={demoMode}
										onDemoFirstTagEditorOpen={
											handleDemoFirstTagEditorOpen
										}
										onDemoTagEditorClose={
											handleDemoTagEditorClose
										}
										onEditorStateChange={
											handleSessionEditorState
										}
									/>
								</div>

								{isFirstSession ? (
									<div
										className={cn(
											'transition-opacity duration-300',
											step === 'record'
												? 'opacity-100'
												: 'hidden opacity-0',
										)}
									>
										<ProntuarioEditableMedicalRecordView
											recordId={recordId}
											patientId={patientId ?? ''}
											medicalRecord={medicalRecord}
											variant="dark"
											demoMode={demoMode}
											onEditorStateChange={
												handleRecordEditorState
											}
										/>
									</div>
								) : null}
							</div>
						</div>
					</div>

					{/* Rodapé fixo com navegação */}
					<div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-gradient-to-t from-[#121A24] via-[#121A24]/90 to-transparent px-6 pb-6 pt-10">
						<div>
							{step === 'record' ? (
								<button
									type="button"
									onClick={handleBack}
									className="pointer-events-auto flex items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/[0.06]"
								>
									<ArrowLeft
										className="size-4"
										strokeWidth={2.5}
										aria-hidden
									/>
									Voltar
								</button>
							) : null}
						</div>
						<button
							type="button"
							onClick={handleAdvance}
							data-tutorial-demo={
								demoMode ? 'review-advance' : undefined
							}
							className="pointer-events-auto flex items-center gap-2 rounded-full bg-[#8ab4f8] px-6 py-2.5 text-sm font-bold text-[#0f1722] shadow-lg transition-colors hover:bg-[#9cc1fa]"
						>
							Avançar
							<ArrowRight
								className="size-4"
								strokeWidth={2.5}
								aria-hidden
							/>
						</button>
					</div>
				</>
			)}

			<SessionEndReviewConfirmModal
				isOpen={confirmOpen}
				isSaving={isSaving}
				demoMode={demoMode}
				onConfirm={handleConfirmSave}
				onCancel={handleCancelConfirm}
			/>
			{showReviewSessionFieldsCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={
						TUTORIAL_DEMO_REVIEW_SESSION_FIELDS_SELECTOR
					}
					backdropTargetSelector={
						TUTORIAL_DEMO_REVIEW_SESSION_FIELDS_SELECTOR
					}
					bodyId="demo-review-coach-session-fields"
					balloonWidthPx={320}
					pinBalloonToViewport="right"
					pinBalloonBesideTargetWhenFits
					compact
					allowTargetInteraction
					showTargetSpotlight={false}
					portalZIndex={10060}
					onContinue={() => {
						setShowSessionFieldsCoach(false);
						setShowSessionTagsCoach(true);
					}}
				>
					A IA pré-preencheu os campos do prontuário com base na
					sessão. Revise presença, humor, queixas, conteúdo trabalhado
					e anotações — tudo é editável antes de evoluir o registro.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showReviewSessionTagsCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_REVIEW_FIRST_ADD_TAG_SELECTOR}
					bodyId="demo-review-coach-session-tags"
					balloonWidthPx={340}
					placement="above"
					compact
					allowTargetInteraction
					portalZIndex={10060}
					onContinue={showAdvanceCoachAfterTour}
				>
					Tags adicionadas automaticamente pela IA com base na sessão
					— você pode editar ou criar novas a qualquer momento.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showReviewSessionTagEditorCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_REVIEW_TAG_EDITOR_SELECTOR}
					bodyId="demo-review-coach-tag-editor"
					balloonWidthPx={340}
					placement="right"
					compact
					allowTargetInteraction
					portalZIndex={10070}
					onContinue={showAdvanceCoachAfterTour}
				>
					Dê um nome ao sinal, escolha se aumentou ou diminuiu na
					sessão e indique se é pior estar alto ou baixo. Quando
					estiver pronto, clique em Salvar.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showReviewSessionAdvanceCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_REVIEW_ADVANCE_SELECTOR}
					bodyId="demo-review-coach-advance"
					balloonWidthPx={380}
					placement="above"
					compact
					showBackdrop={false}
					showTargetSpotlight={false}
					showContinueButton={false}
					portalZIndex={10060}
					onContinue={() => setShowSessionAdvanceCoach(false)}
				>
					Fique à vontade para explorar. Quando quiser, clique aqui em{' '}
					<span className="font-bold text-[#8ab4f8]">Avançar</span>
					<ArrowRight
						className="mx-0.5 inline size-3.5 align-text-bottom text-[#8ab4f8]"
						strokeWidth={2.5}
						aria-hidden
					/>{' '}
					.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showReviewRecordFieldsCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_REVIEW_RECORD_FIELDS_SELECTOR}
					backdropTargetSelector={
						TUTORIAL_DEMO_REVIEW_RECORD_FIELDS_SELECTOR
					}
					bodyId="demo-review-coach-record-fields"
					balloonWidthPx={360}
					pinBalloonToViewport="right"
					pinBalloonBesideTargetWhenFits
					compact
					allowTargetInteraction
					showTargetSpotlight={false}
					portalZIndex={10060}
					onContinue={completeRecordFieldsCoach}
				>
					A IA pré-preencheu o histórico clínico e o plano terapêutico
					com base nesta sessão — revise e edite antes de evoluir o
					registro. Isso aparece apenas na primeira sessão de cada
					paciente; nas próximas, histórico e plano continuam
					acessíveis pelo prontuário do paciente.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showReviewRecordAdvanceCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_REVIEW_ADVANCE_SELECTOR}
					bodyId="demo-review-coach-record-advance"
					balloonWidthPx={380}
					placement="above"
					compact
					showBackdrop={false}
					showTargetSpotlight={false}
					showContinueButton={false}
					portalZIndex={10060}
					onContinue={() => setShowRecordAdvanceCoach(false)}
				>
					Fique à vontade para explorar. Quando quiser, clique aqui em{' '}
					<span className="font-bold text-[#8ab4f8]">Avançar</span>
					<ArrowRight
						className="mx-0.5 inline size-3.5 align-text-bottom text-[#8ab4f8]"
						strokeWidth={2.5}
						aria-hidden
					/>{' '}
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showReviewConfirmCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_REVIEW_EVOLUIR_MODAL_SELECTOR}
					bodyId="demo-review-coach-evoluir-confirm"
					balloonWidthPx={360}
					placement="above"
					compact
					allowTargetInteraction
					allowHeaderInteraction={false}
					showContinueButton={false}
					portalZIndex={10070}
					onContinue={() => setShowConfirmCoach(false)}
				>
					Confirme quando estiver pronto: ao evoluir o prontuário,
					todas as suas edições — da sessão, do histórico e do plano —
					serão salvas e aplicadas ao registro do paciente.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
		</motion.div>
	);
};

export { SessionEndProntuarioReview };
